import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faWandMagicSparkles,
  faCopy,
  faPaperPlane,
  faRotateRight,
  faBolt,
  faFileLines,
  faReply,
  faFileInvoiceDollar,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";

const POPOVER_W = 408;
const MARGIN = 12;

function rectStyle(anchorRect) {
  if (!anchorRect || typeof anchorRect.top !== "number") {
    return { top: MARGIN, left: MARGIN };
  }
  let left = anchorRect.right + MARGIN;
  let top = anchorRect.top;
  if (left + POPOVER_W > window.innerWidth - MARGIN) {
    left = anchorRect.left - POPOVER_W - MARGIN;
  }
  if (left < MARGIN) left = MARGIN;
  if (top + 360 > window.innerHeight - MARGIN) {
    top = Math.max(MARGIN, window.innerHeight - 380);
  }
  return { top, left };
}

function SummaryBody({ text }) {
  const lines = (text || "").split(/\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-violet-200/80 bg-violet-50/30 py-6 text-center dark:border-violet-500/20 dark:bg-violet-950/20">
        <FontAwesomeIcon icon={faFileLines} className="text-[18px] text-violet-300 dark:text-violet-500/60" />
        <p className="text-[11.5px] text-slate-500 dark:text-slate-400">No summary available.</p>
      </div>
    );
  }
  return (
    <ul className="list-none space-y-2 pl-0">
      {lines.map((line, i) => (
        <motion.li
          key={i}
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.05, duration: 0.22 }}
          className="flex gap-2.5 rounded-lg border border-transparent px-2 py-1.5 text-[12.5px] leading-snug text-slate-700 transition hover:border-violet-200/60 hover:bg-violet-50/40 dark:text-slate-200 dark:hover:border-violet-500/20 dark:hover:bg-violet-950/30"
        >
          <span
            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 shadow-[0_0_6px_rgba(139,92,246,0.5)]"
            aria-hidden
          />
          <span>{line.replace(/^[-•]\s*/, "")}</span>
        </motion.li>
      ))}
    </ul>
  );
}

function LoadingBlock() {
  const steps = ["Reading message", "Drafting summary", "Suggesting reply"];
  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center gap-3">
        {/* Mini icon tile with pulsing glow + 3 orbiting particles */}
        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-2xl bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)]"
            animate={{
              boxShadow: [
                "0 8px 22px -6px rgba(124,58,237,0.55)",
                "0 12px 30px -6px rgba(124,58,237,0.85)",
                "0 8px 22px -6px rgba(124,58,237,0.55)",
              ],
              scale: [1, 1.04, 1],
            }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          />
          <motion.span
            className="relative text-white"
            animate={{ rotate: [0, 12, -8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[18px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]" />
          </motion.span>
          {[0, 1, 2].map((i) => {
            const angle = (i / 3) * Math.PI * 2;
            const rx = Math.cos(angle) * 22;
            const ry = Math.sin(angle) * 22;
            return (
              <motion.span
                key={i}
                aria-hidden
                className="absolute left-1/2 top-1/2 h-1 w-1 rounded-full bg-violet-400 shadow-[0_0_6px_rgba(167,139,250,0.85)]"
                style={{ translateX: "-50%", translateY: "-50%" }}
                animate={{
                  x: [0, rx, 0],
                  y: [0, ry, 0],
                  opacity: [0, 1, 0],
                  scale: [0.5, 1, 0.5],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2.2,
                  delay: i * 0.18,
                  ease: "easeInOut",
                }}
              />
            );
          })}
        </div>
        <div className="flex flex-1 flex-wrap gap-1.5">
          {steps.map((label, i) => (
            <motion.span
              key={label}
              className="inline-flex items-center gap-1 rounded-full border border-violet-200/80 bg-violet-50/90 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-800 dark:border-violet-500/30 dark:bg-violet-950/50 dark:text-violet-200"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ repeat: Infinity, duration: 1.6, delay: i * 0.2, ease: "easeInOut" }}
            >
              <motion.span
                aria-hidden
                className="h-1 w-1 rounded-full bg-violet-500"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1.1, delay: i * 0.15 }}
              />
              {label}
            </motion.span>
          ))}
        </div>
      </div>
      {/* Skeleton lines */}
      <div className="space-y-1.5 pt-1">
        {[100, 80, 92, 60].map((w, i) => (
          <motion.div
            key={i}
            className="h-2 rounded-full bg-gradient-to-r from-violet-100 via-indigo-100 to-violet-100 dark:from-violet-950/60 dark:via-indigo-950/60 dark:to-violet-950/60"
            style={{ width: `${w}%` }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 1.4, delay: i * 0.12, ease: "easeInOut" }}
          />
        ))}
      </div>
    </div>
  );
}

function fmtExtractNumber(value, fallback = "—") {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return fallback;
  return n.toString();
}

// Turn a rate-schema key (e.g. "non-stackable") into a human label ("Non stackable").
function prettyLabel(key) {
  const s = String(key || "").replace(/[-_]+/g, " ").trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ExtractRows({ kind, extractedData, extractedWanted }) {
  if (kind === "airline") {
    const ffwh = extractedData?.FFWH || {};
    const sp = extractedData?.screeningPrices || {};
    const cur = extractedData?.currency || "£";

    // Build a per_kg/min_charge line item; returns null when both are 0/absent
    // so we can drop empty rows and only surface real extracted values.
    const perKgRow = (label, obj) => {
      const p = Number(obj?.per_kg || 0);
      const m = Number(obj?.min_charge || 0);
      if (!p && !m) return null;
      return {
        label,
        value: `${cur}${fmtExtractNumber(p)}/kg${
          m ? ` (min ${cur}${fmtExtractNumber(m)})` : ""
        }`,
      };
    };

    // BUG B fix (display): freight falls back to the wanted/grid rate when
    // FFWH.freightCharge is 0, mirroring the automated consumer.
    const freightPerKg =
      Number(ffwh.freightCharge?.per_kg) > 0
        ? Number(ffwh.freightCharge.per_kg)
        : Number(extractedWanted?.per_kg || 0);
    const freightMin =
      Number(ffwh.freightCharge?.per_kg) > 0
        ? Number(ffwh.freightCharge?.min_charge || 0)
        : Number(extractedWanted?.min_charge || 0);

    const rows = [
      // Wanted rate (kept as today, now currency-aware).
      extractedWanted?.per_kg
        ? {
            label: "Wanted rate",
            value: `${cur}${fmtExtractNumber(extractedWanted.per_kg)}/kg${
              extractedWanted.min_charge
                ? ` (min ${cur}${fmtExtractNumber(extractedWanted.min_charge)})`
                : ""
            }`,
          }
        : { label: "Wanted rate", value: "—" },
      // FFWH line items (freight uses the wanted-rate fallback).
      perKgRow("Freight charge", { per_kg: freightPerKg, min_charge: freightMin }),
      perKgRow("Fuel surcharge", ffwh.fuelSurcharge),
      perKgRow("War risk surcharge", ffwh.warRiskSurcharge),
      perKgRow("Handling fee", ffwh.handlingFee),
      perKgRow("Security charge", ffwh.securityCharge),
      perKgRow("Storage charge", ffwh.storageCharge),
      // Screening prices.
      perKgRow("Primary screening", sp.primaryScreeningPrice),
      perKgRow("Secondary screening", sp.secondaryScreeningPrice),
      // Full cargo-category rate grid.
      ...Object.entries(extractedData?.rates || {}).map(([key, val]) =>
        perKgRow(`Rate: ${prettyLabel(key)}`, val)
      ),
      // Non-priced meta rows, only when present.
      ffwh.transitTimeDays
        ? { label: "Transit time", value: `${ffwh.transitTimeDays} days` }
        : null,
      extractedData?.valid_until
        ? { label: "Validity", value: extractedData.valid_until }
        : null,
    ].filter(Boolean);

    if (!rows.length) {
      return (
        <div className="rounded-lg border border-slate-200/70 bg-white/80 px-2.5 py-3 text-center text-[11.5px] text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)] dark:text-slate-500">
          No rate values found
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between gap-2 rounded-lg border border-slate-200/70 bg-white/80 px-2.5 py-1.5 text-[11.5px] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)]"
          >
            <span className="truncate text-slate-500 dark:text-slate-400">
              {r.label}
            </span>
            <span
              className="truncate text-right font-medium text-slate-800 dark:text-slate-100"
              title={String(r.value)}
            >
              {r.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // agent
  const sp = extractedData?.screeningPrices || {};
  const currency = extractedData?.currency || "£";
  const rows = [
    {
      label: "Final cost",
      value:
        extractedData?.finalCost != null && Number(extractedData.finalCost) > 0
          ? `${currency}${fmtExtractNumber(extractedData.finalCost)}`
          : "—",
    },
    {
      label: "Other (freight)",
      value: sp.otherScreeningPrice?.per_kg
        ? `${currency}${fmtExtractNumber(sp.otherScreeningPrice.per_kg)}${
            sp.otherScreeningPrice.min_charge
              ? ` (min ${currency}${fmtExtractNumber(sp.otherScreeningPrice.min_charge)})`
              : ""
          }`
        : "—",
    },
    {
      label: "Primary screening",
      value: sp.primaryScreeningPrice?.per_kg
        ? `${currency}${fmtExtractNumber(sp.primaryScreeningPrice.per_kg)}${
            sp.primaryScreeningPrice.min_charge
              ? ` (min ${currency}${fmtExtractNumber(sp.primaryScreeningPrice.min_charge)})`
              : ""
          }`
        : "—",
    },
    {
      label: "Secondary screening",
      value: sp.secondaryScreeningPrice?.per_kg
        ? `${currency}${fmtExtractNumber(sp.secondaryScreeningPrice.per_kg)}${
            sp.secondaryScreeningPrice.min_charge
              ? ` (min ${currency}${fmtExtractNumber(sp.secondaryScreeningPrice.min_charge)})`
              : ""
          }`
        : "—",
    },
    {
      label: "Destination airport",
      value: sp.destinationAirport || "—",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {rows.map((r) => (
        <div
          key={r.label}
          className="flex items-center justify-between gap-2 rounded-lg border border-slate-200/70 bg-white/80 px-2.5 py-1.5 text-[11.5px] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)]"
        >
          <span className="truncate text-slate-500 dark:text-slate-400">
            {r.label}
          </span>
          <span
            className="truncate text-right font-medium text-slate-800 dark:text-slate-100"
            title={String(r.value)}
          >
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AiMessageAssistPopover({
  open,
  anchorRect,
  loading = false,
  error = null,
  data = { summary: null, suggestedReply: null, cached: false, generatedAt: null },
  onClose,
  onRegenerate,
  onUseReply,
  onCopy,
  // Extract-rates tab (only rendered when kind is "airline" | "agent")
  kind,
  onExtract,
  extractLoading = false,
  extractError = null,
  extractedData = null,
  extractedWanted = null,
  onApply,
  applying = false,
}) {
  const rootRef = useRef(null);
  const [tab, setTab] = useState("summary");
  const [replyDraft, setReplyDraft] = useState("");
  const extractAutoFiredRef = useRef(false);
  const showExtractTab = kind === "airline" || kind === "agent";

  useEffect(() => {
    if (data?.suggestedReply != null) setReplyDraft(data.suggestedReply);
  }, [data?.suggestedReply, data?.generatedAt]);

  // Reset auto-trigger ref when popover closes/opens.
  useEffect(() => {
    if (!open) {
      extractAutoFiredRef.current = false;
      // Keep tab on summary by default for next open; extract tab only sticks while open.
      setTab("summary");
    }
  }, [open]);

  // Auto-fire extract on first activation of the extract tab when there's
  // no data yet, no error, and we aren't already loading. Gated by ref so
  // it only fires once per open.
  useEffect(() => {
    if (!open) return;
    if (tab !== "extract") return;
    if (!showExtractTab) return;
    if (extractAutoFiredRef.current) return;
    if (extractedData) return;
    if (extractLoading) return;
    if (extractError) return;
    extractAutoFiredRef.current = true;
    onExtract?.();
  }, [open, tab, showExtractTab, extractedData, extractLoading, extractError, onExtract]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      if (e.target.closest?.("[data-fk-ai-assist-anchor]")) return;
      onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose]);

  const pos = useMemo(() => rectStyle(anchorRect), [anchorRect]);

  const assistDisabled = loading;
  const wandMotion = assistDisabled
    ? { animate: false }
    : {
        animate: { rotate: [0, -7, 0, 7, 0], scale: [1, 1.05, 1, 1.05, 1] },
        transition: { repeat: Infinity, duration: 4.5, ease: "easeInOut" },
        whileHover: { rotate: [0, -14, 14, -10, 0], transition: { duration: 0.45 } },
      };

  if (!open) return null;

  const tabs = [
    { key: "summary", label: "Summary", icon: faFileLines },
    { key: "reply", label: "Suggested reply", icon: faReply },
    ...(showExtractTab
      ? [{ key: "extract", label: "Extract rates", icon: faFileInvoiceDollar }]
      : []),
  ];

  // Body-level loading/error gating only applies to summary/reply (which share
  // the existing Message-Assist API call). The extract tab manages its own
  // loading/error from its own props so we don't conflate the two.
  const showSharedLoading = loading && tab !== "extract";
  const showSharedError = error && !loading && tab !== "extract";

  const node = (
    <AnimatePresence>
      <motion.div
        ref={rootRef}
        data-ai-assist-popover="1"
        initial={{ opacity: 0, y: 6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 6, scale: 0.98 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="fixed z-[190] overflow-hidden rounded-2xl border border-violet-200/90 bg-white shadow-[0_24px_60px_-18px_rgba(124,58,237,0.45)] dark:border-violet-500/25 dark:bg-[oklch(0.225_0.026_264)]"
        style={{ top: pos.top, left: pos.left, width: POPOVER_W }}
      >
        {/* Animated rotating conic ring (very subtle) */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-[1.05rem] opacity-30"
          style={{
            background:
              "conic-gradient(from 0deg, #a78bfa, #60a5fa, #818cf8, #c4b5fd, #a78bfa, #60a5fa, #a78bfa)",
          }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
        />
        <div className="relative rounded-2xl bg-white dark:bg-[oklch(0.225_0.026_264)]">
          {/* Top shimmer bar */}
          <div className="relative h-[3px] w-full overflow-hidden bg-violet-100 dark:bg-violet-950/60">
            <motion.div
              className="absolute inset-y-0 w-1/3 bg-[linear-gradient(90deg,transparent,#8b5cf6_30%,#6366f1_50%,#3b82f6_70%,transparent)]"
              animate={{ x: ["-120%", "320%"] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            />
          </div>

          {/* Header */}
          <div className="relative border-b border-violet-100/80 bg-gradient-to-br from-violet-50/90 via-white to-indigo-50/60 px-3 pb-2 pt-3 dark:border-violet-500/15 dark:from-violet-950/50 dark:via-slate-900 dark:to-indigo-950/35">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)] text-white shadow-[0_8px_22px_-6px_rgba(124,58,237,0.55)]">
                  <motion.span className="inline-flex" {...wandMotion}>
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[14px]" />
                  </motion.span>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-[13px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                      Message assist
                    </h3>
                    <span className="inline-flex items-center gap-0.5 rounded-full border border-violet-200/80 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-violet-700 shadow-sm dark:border-violet-500/30 dark:bg-[oklch(0.225_0.026_264)] dark:text-violet-300">
                      <FontAwesomeIcon icon={faBolt} className="text-[7px]" />
                      AI
                    </span>
                  </div>
                  <p className="text-[10.5px] text-slate-500 dark:text-slate-400">
                    Read-only · doesn&apos;t touch the form
                  </p>
                </div>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => onClose?.()}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/80 hover:text-slate-700 dark:hover:bg-[oklch(0.26_0.028_264)]"
              >
                <FontAwesomeIcon icon={faTimes} className="text-[11px]" />
              </button>
            </div>

            {/* Tabs with sliding active pill */}
            <div className="mt-2.5 flex rounded-xl border border-violet-200/60 bg-white/70 p-0.5 dark:border-violet-500/20 dark:bg-[oklch(0.225_0.026_264/0.6)]">
              {tabs.map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.key)}
                    className="relative flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition"
                  >
                    {active ? (
                      <motion.span
                        layoutId="ai-assist-tab-pill"
                        transition={{ type: "spring", stiffness: 380, damping: 28 }}
                        className="absolute inset-0 rounded-lg bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#4f46e5_100%)] shadow-[0_4px_12px_-4px_rgba(124,58,237,0.6)]"
                      />
                    ) : null}
                    <span
                      className={`relative inline-flex items-center gap-1.5 ${
                        active
                          ? "text-white"
                          : "text-slate-600 hover:text-violet-700 dark:text-slate-300 dark:hover:text-violet-300"
                      }`}
                    >
                      <FontAwesomeIcon icon={t.icon} className="text-[9px]" />
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div className="relative max-h-[min(58vh,24rem)] overflow-y-auto p-3 sm:p-4">
            {showSharedLoading ? <LoadingBlock /> : null}

            {showSharedError ? (
              <div className="rounded-xl border border-rose-200/80 bg-rose-50/90 px-3 py-2.5 text-[12px] text-rose-800 dark:border-rose-500/30 dark:bg-rose-950/40 dark:text-rose-200">
                <p className="font-medium">{error}</p>
                <button
                  type="button"
                  onClick={() => onRegenerate?.()}
                  className="mt-2 inline-flex items-center gap-1 rounded-md bg-rose-100/70 px-2 py-1 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-200/70 dark:bg-rose-900/40 dark:text-rose-200 dark:hover:bg-rose-900/70"
                >
                  <FontAwesomeIcon icon={faRotateRight} className="text-[9px]" />
                  Retry
                </button>
              </div>
            ) : null}

            {!loading && !error && tab === "summary" ? (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <SummaryBody text={data.summary} />
                {data.summary ? (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onCopy?.(data.summary || "")}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 hover:shadow-[0_4px_12px_-4px_rgba(124,58,237,0.3)] dark:border-violet-500/30 dark:bg-[oklch(0.26_0.028_264)] dark:text-violet-300 dark:hover:bg-violet-950/50"
                    >
                      <FontAwesomeIcon icon={faCopy} className="text-[9px]" />
                      Copy summary
                    </button>
                  </div>
                ) : null}
              </motion.div>
            ) : null}

            {!loading && !error && tab === "reply" ? (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                    Suggested reply
                  </label>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    {replyDraft.length} chars
                  </span>
                </div>
                <div className="relative">
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 left-0 w-[2px] rounded-l-xl bg-gradient-to-b from-violet-400 via-indigo-400 to-blue-400"
                  />
                  <textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    rows={8}
                    className="w-full resize-y rounded-xl border border-slate-200 bg-white pl-3.5 pr-3 py-2.5 text-[12.5px] leading-relaxed text-slate-800 shadow-inner outline-none transition focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(139,92,246,0.12)] dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.168_0.021_264)] dark:text-slate-100"
                    placeholder="AI will draft a polite reply here…"
                  />
                </div>
                <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => onRegenerate?.()}
                    className="group inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold text-violet-600 transition hover:bg-violet-50 hover:text-violet-700 dark:text-violet-400 dark:hover:bg-violet-950/50"
                  >
                    <FontAwesomeIcon
                      icon={faRotateRight}
                      className="text-[9px] transition group-hover:rotate-180"
                    />
                    Regenerate
                  </button>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => onCopy?.(replyDraft)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 dark:border-violet-500/30 dark:bg-[oklch(0.26_0.028_264)] dark:text-violet-300 dark:hover:bg-violet-950/50"
                    >
                      <FontAwesomeIcon icon={faCopy} className="text-[9px]" />
                      Copy
                    </button>
                    {typeof onUseReply === "function" ? (
                      <button
                        type="button"
                        onClick={() => onUseReply?.(replyDraft)}
                        className="group relative inline-flex items-center gap-1.5 overflow-hidden rounded-lg bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)] px-3 py-1.5 text-[11px] font-semibold text-white shadow-[0_6px_18px_-6px_rgba(124,58,237,0.6)] transition-all hover:shadow-[0_10px_24px_-6px_rgba(124,58,237,0.8)] hover:brightness-110"
                      >
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)] transition-transform duration-700 group-hover:translate-x-full"
                        />
                        <FontAwesomeIcon icon={faPaperPlane} className="relative text-[9px]" />
                        <span className="relative">Use this reply</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ) : null}

            {showExtractTab && tab === "extract" ? (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {extractLoading ? (
                  <LoadingBlock />
                ) : extractError ? (
                  <div className="rounded-xl border border-rose-200/80 bg-rose-50/90 px-3 py-2.5 text-[12px] text-rose-800 dark:border-rose-500/30 dark:bg-rose-950/40 dark:text-rose-200">
                    <p className="font-medium">{extractError}</p>
                    <button
                      type="button"
                      onClick={() => onExtract?.()}
                      className="mt-2 inline-flex items-center gap-1 rounded-md bg-rose-100/70 px-2 py-1 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-200/70 dark:bg-rose-900/40 dark:text-rose-200 dark:hover:bg-rose-900/70"
                    >
                      <FontAwesomeIcon icon={faRotateRight} className="text-[9px]" />
                      Retry
                    </button>
                  </div>
                ) : extractedData ? (
                  <div className="space-y-3">
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                        {kind === "airline"
                          ? "Extracted airline rate"
                          : "Extracted transporter rate"}
                      </label>
                    </div>
                    <ExtractRows
                      kind={kind}
                      extractedData={extractedData}
                      extractedWanted={extractedWanted}
                    />
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => onApply?.(extractedData)}
                        disabled={Boolean(applying)}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_45%,#4f46e5_100%)] px-3 py-2 text-[11.5px] font-semibold text-white shadow-[0_8px_22px_-8px_rgba(124,58,237,0.65)] transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FontAwesomeIcon
                          icon={applying ? faCircleNotch : faFileInvoiceDollar}
                          className={`text-[10px] ${applying ? "animate-spin" : ""}`}
                        />
                        {applying ? "Applying…" : "Apply to charges"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[12px] text-slate-600 dark:text-slate-300">
                      Pull rate fields out of this message with AI and apply
                      them to the saved {kind === "airline" ? "airline" : "transporter"} rate.
                    </p>
                    <button
                      type="button"
                      onClick={() => onExtract?.()}
                      className="group relative inline-flex w-full items-center justify-center gap-1.5 overflow-hidden rounded-lg bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)] px-3 py-2 text-[11.5px] font-semibold text-white shadow-[0_6px_18px_-6px_rgba(124,58,237,0.6)] transition-all hover:shadow-[0_10px_24px_-6px_rgba(124,58,237,0.8)] hover:brightness-110"
                    >
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)] transition-transform duration-700 group-hover:translate-x-full"
                      />
                      <FontAwesomeIcon icon={faFileInvoiceDollar} className="relative text-[10px]" />
                      <span className="relative">Extract rates</span>
                    </button>
                  </div>
                )}
              </motion.div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-slate-100 bg-gradient-to-br from-white via-violet-50/30 to-white px-3 py-2 dark:border-[oklch(0.3_0.02_264)] dark:from-slate-900 dark:via-violet-950/15 dark:to-slate-900">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] ${
                data?.cached
                  ? "border-slate-200 bg-white/80 text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.8)] dark:text-slate-400"
                  : "border-violet-200/80 bg-violet-50/80 text-violet-700 dark:border-violet-500/25 dark:bg-violet-950/40 dark:text-violet-300"
              }`}
            >
              <span
                className={`h-1 w-1 rounded-full ${data?.cached ? "bg-slate-400" : "bg-violet-500"}`}
              />
              {data?.cached ? "Cached" : "Generated"}
            </span>
            {data?.generatedAt ? (
              <span className="text-[10px] text-slate-400 dark:text-slate-500">{data.generatedAt}</span>
            ) : null}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(node, document.body);
}
